import { Link } from 'react-router-dom';
import type { ContactField, VaultItem } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { resolveLinkedItems } from '../binder/resolveLinkedItems';
import { describeRecurrence } from '../binder/recurrence';
import { Icon, type IconName } from './hbs/Icon';

interface Props {
  orgId: string;
  relatedItemIds?: string[];
  categoryId?: string;
  /** The id of the item being viewed. When set, schedules whose own
   *  `relatedItemId` points at it (reverse links) are surfaced too. */
  itemId?: string;
}

/** First non-empty value in a contact field list (phones / emails). */
function firstValue(fields?: ContactField[]): string | undefined {
  return fields?.map((f) => f.value.trim()).find((v) => v.length > 0);
}

/**
 * Renders a document's linked items grouped by type: Binder items (contacts,
 * documents, schedules) and family-shared Safe items (logins, notes, cards,
 * identities). Each links to its detail page; a schedule also shows its
 * recurrence summary.
 *
 * A Safe item renders as **name only**. Its username, URL and secrets stay on
 * the item's own page, where that item's sensitive-display rules govern them —
 * a document view is not a second, ungoverned place for them to appear. Unresolvable links are skipped by `resolveLinkedItems`, so
 * nothing broken is ever shown.
 *
 * A linked contact also surfaces its first phone + email as live `tel:` /
 * `mailto:` quick-actions, so a document (e.g. House Maintenance) is one click
 * from calling or emailing the linked contact (e.g. the plumber) without
 * opening the contact's detail page. Same href conventions as ContactDetailPage.
 */
export default function LinkedItemsSection({ orgId, relatedItemIds, categoryId, itemId }: Props): JSX.Element | null {
  const { items } = useSafe();
  const { contacts, documents, schedules, safeItems } = resolveLinkedItems(relatedItemIds, items, orgId, itemId);
  if (contacts.length + documents.length + schedules.length + safeItems.length === 0) return null;

  const contactHref = (i: VaultItem): string =>
    i.categoryId
      ? `/orgs/${orgId}/binder/${i.categoryId}/contacts/${i.id}`
      : `/orgs/${orgId}/binder/contacts/${i.id}`;
  const documentHref = (i: VaultItem): string =>
    `/orgs/${orgId}/binder/${i.categoryId ?? categoryId}/documents/${i.id}`;
  const scheduleHref = (i: VaultItem): string =>
    `/orgs/${orgId}/binder/${i.categoryId ?? categoryId}/schedules/${i.id}`;
  // Family-shared Safe items live outside the Binder's category tree.
  const safeItemHref = (i: VaultItem): string => `/orgs/${orgId}/items/${i.id}`;
  // resolveLinkedItems only ever yields these four types, but it cannot say so
  // in the type system — an explicit map keeps that guarantee visible here and
  // fails closed on a type the icon set does not cover.
  const SAFE_ICON: Record<string, IconName> = {
    login: 'login', note: 'note', card: 'card', identity: 'identity',
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">Linked items</div>
      <ul className="flex flex-col gap-1">
        {contacts.map((i) => {
          const phone = firstValue(i.phones);
          const email = firstValue(i.emails);
          return (
            <li key={i.id} className="flex flex-col gap-0.5">
              <Link to={contactHref(i)} className="inline-flex items-center gap-2 text-[14px] text-hbs-primary hover:underline">
                <Icon name="contact" size={14} /> {i.name}
              </Link>
              {(phone || email) && (
                <div className="ml-[22px] flex flex-wrap items-center gap-x-4 gap-y-0.5">
                  {phone && (
                    <a
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      aria-label={`Call ${i.name}`}
                      className="inline-flex items-center gap-1 text-[13px] text-hbs-text-mute hover:text-hbs-primary hover:underline"
                    >
                      <Icon name="phone" size={12} /> {phone}
                    </a>
                  )}
                  {email && (
                    <a
                      href={`mailto:${email}`}
                      aria-label={`Email ${i.name}`}
                      className="inline-flex items-center gap-1 text-[13px] text-hbs-text-mute hover:text-hbs-primary hover:underline"
                    >
                      <Icon name="mail" size={12} /> {email}
                    </a>
                  )}
                </div>
              )}
            </li>
          );
        })}
        {documents.map((i) => (
          <li key={i.id}>
            <Link to={documentHref(i)} className="inline-flex items-center gap-2 text-[14px] text-hbs-primary hover:underline">
              <Icon name="note" size={14} /> {i.name}
            </Link>
          </li>
        ))}
        {safeItems.map((i) => (
          <li key={i.id}>
            <Link to={safeItemHref(i)} className="inline-flex items-center gap-2 text-[14px] text-hbs-primary hover:underline">
              <Icon name={SAFE_ICON[i.type] ?? 'note'} size={14} /> {i.name}
            </Link>
          </li>
        ))}
        {schedules.map((i) => (
          <li key={i.id}>
            <Link to={scheduleHref(i)} className="inline-flex items-center gap-2 text-[14px] text-hbs-primary hover:underline">
              <Icon name="calendar" size={14} /> {i.name}
              <span className="text-[12px] text-hbs-text-mute">· {describeRecurrence(i.rrule)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
